import { useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";
import {
  buildCreatePlaybackStreamCommandPayload,
  getCreatePlaybackStreamCommandError,
} from "../lib/createPlaybackStreamCommand";

/**
 * Portal step for playback — executeSurveillanceCommand (createPlaybackStream).
 * Each call uses its own request id so a newer seek can supersede an older one.
 */
export function useCreatePlaybackStreamCommand({
  standalone,
  entId,
  orgId,
  siteId,
  isAuthReady,
}) {
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const pendingRef = useRef({});

  useEffect(() => {
    Object.keys(pendingRef.current).forEach((requestUuid) => {
      if (!(requestUuid in dataMap)) return;

      const response = getData(requestUuid);
      const pending = pendingRef.current[requestUuid];
      delete pendingRef.current[requestUuid];
      if (!pending) return;

      const commandError = getCreatePlaybackStreamCommandError(response);
      if (commandError) {
        pending.reject(new Error(commandError));
        return;
      }

      pending.resolve(response);
    });
  }, [dataMap, getData]);

  const requestCreatePlaybackStream = useCallback(
    ({
      deviceId,
      streamId = 1,
      startTime,
      endTime,
      siteId: overrideSiteId,
    }) => {
      if (!isAuthReady || !orgId || !deviceId) {
        return Promise.reject(
          new Error("Missing scope or device for createPlaybackStream")
        );
      }

      if (!Number.isFinite(Number(startTime)) || !Number.isFinite(Number(endTime))) {
        return Promise.reject(
          new Error("createPlaybackStream requires valid startTime and endTime")
        );
      }

      const effectiveSiteId = overrideSiteId || siteId;
      if (!effectiveSiteId) {
        return Promise.reject(
          new Error("Missing siteId for createPlaybackStream")
        );
      }

      const requestUuid = uuidv4();
      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update";
      const restfulParams = standalone
        ? { orgId, siteId: effectiveSiteId, deviceId }
        : { entId, orgId, siteId: effectiveSiteId, deviceId };

      return new Promise((resolve, reject) => {
        pendingRef.current[requestUuid] = { deviceId, resolve, reject };
        sendRequest(
          apiKey,
          requestUuid,
          restfulParams,
          buildCreatePlaybackStreamCommandPayload({
            streamId,
            startTime,
            endTime,
          })
        );
      });
    },
    [standalone, entId, orgId, siteId, isAuthReady, sendRequest]
  );

  const cancelPending = useCallback(() => {
    Object.values(pendingRef.current).forEach((pending) => {
      pending.reject(new Error("createPlaybackStream superseded"));
    });
    pendingRef.current = {};
  }, []);

  return { requestCreatePlaybackStream, cancelPending };
}
